/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The audit endpoints, and the one place filter state becomes a query string.
 *
 * `buildAuditQuery` is shared by the list, the facets and the export so the three can never
 * disagree about what "the current filter set" means — an export that covers a different set from
 * the one on screen would be worse than no export.
 */

import type { ApiService } from '@/types/dashboard-types'

import { resolveRange } from './format'
import type { AuditDetail, AuditFacets, AuditFilters, AuditListResponse } from './types'

/**
 * Filters as query parameters. Arrays become repeated keys, which the API reads as a multi-value
 * filter; empty values are omitted entirely rather than sent blank.
 */
export const buildAuditQuery = (filters: AuditFilters): URLSearchParams => {
  const params = new URLSearchParams()
  const { after, before } = resolveRange(filters)

  filters.companyIds.forEach((id) => params.append('companyId', String(id)))
  filters.changeTypes.forEach((v) => params.append('changeType', v))
  filters.entityTypes.forEach((v) => params.append('entityType', v))
  filters.userIds.forEach((v) => params.append('userId', v))

  if (after) params.set('after', after)
  if (before) params.set('before', before)
  if (filters.entityId) params.set('entityId', filters.entityId)
  if (filters.search.trim()) params.set('search', filters.search.trim())

  return params
}

export const fetchAudits = (
  api: ApiService,
  filters: AuditFilters,
  { cursor, take }: { cursor?: string; take: number }
): Promise<AuditListResponse> => {
  const params = buildAuditQuery(filters)
  params.set('take', String(take))
  // The count is the expensive half of the query and is not needed to scroll — the result summary
  // comes from the facets endpoint instead.
  params.set('includeCount', 'false')
  if (cursor) params.set('cursor', cursor)

  return api.simpleGet<AuditListResponse>({ path: `audits?${params.toString()}`, origin: 'auditLogs' })
}

export const fetchAuditDetail = (api: ApiService, id: string): Promise<{ data: AuditDetail }> =>
  api.simpleGet<{ data: AuditDetail }>({
    path: `audits/${encodeURIComponent(id)}`,
    origin: 'auditLogs',
  })

export const fetchAuditFacets = (api: ApiService, filters: AuditFilters): Promise<{ data: AuditFacets }> =>
  api.simpleGet<{ data: AuditFacets }>({
    path: `audits/facets?${buildAuditQuery(filters).toString()}`,
    origin: 'auditLogs',
  })
